/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Title,
} from "chart.js";
import axios from "../../../../util/axiosconfig";
import { showError } from "../../../../util/ShowStatus";
import { getErrorMessage } from "../../../../helper/getResponseMessage";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import DashboardCard from "./DashBoardCard";
import PieCharts from "./PieCharts";
import BarCharts from "./BarCharts";

ChartJS.register(
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard: React.FC = () => {
  const [dashBoardDataLoading, setDashBoardDataLoading] =
    useState<boolean>(false);

  const [createdPieChartDataLoading, setCreatedPieChartDataLoading] =
    useState<boolean>(false);
  const [entryPieChartDataLoading, setEntryPieChartDataLoading] =
    useState<boolean>(false);
  const [isFirstTimeLoaded, setIsFirstTimeLoaded] = useState<boolean>(true);
  const [dashBoardData, setDashBoardData] = useState<any>(null);

  const formatFilterDate = (date: Date | null): string | null => {
    if (!date) return null;
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0]; // Format as 'YYYY-MM-DD'
  };
  const [createdByFilter, setCreatedByFilter] = useState<any>({
    createdByStartDate: formatFilterDate(
      new Date(new Date().setDate(new Date().getDate() - 1))
    ),
    createdByEndDate: formatFilterDate(
      new Date(new Date().setDate(new Date().getDate() - 1))
    ),
  });

  const [enteredByFilter, setEnteredByFilter] = useState<any>({
    enteredByStartDate: formatFilterDate(
      new Date(new Date().setDate(new Date().getDate() - 1))
    ),
    enteredByEndDate: formatFilterDate(
      new Date(new Date().setDate(new Date().getDate() - 1))
    ),
  });

  const [pieType, setPieType] = useState<string>("createdBy");

  const toast = useRef<Toast>(null);
  const fetchDashBoardData = async (startDate?: any, endDate?: any) => {
    try {
      const params: any = {};
      if (isFirstTimeLoaded) {
        setDashBoardDataLoading(true);
        params.invoiceCreatedByStartDate = startDate;
        params.invoiceCreatedByEndDate = endDate;
        params.invoiceEnteredByStartDate = startDate;
        params.invoiceEnteredByEndDate = endDate;
      }
      if (pieType === "createdBy") {
        setCreatedPieChartDataLoading(true);
      } else if (pieType === "enteredBy") {
        setEntryPieChartDataLoading(true);
      }

      if (createdByFilter.createdByStartDate) {
        params.invoiceCreatedByStartDate = createdByFilter.createdByStartDate;
      }

      if (createdByFilter.createdByEndDate) {
        params.invoiceCreatedByEndDate = createdByFilter.createdByEndDate;
      }

      if (enteredByFilter.enteredByStartDate) {
        params.invoiceEnteredByStartDate = enteredByFilter.enteredByStartDate;
      }

      if (enteredByFilter.enteredByEndDate) {
        params.invoiceEnteredByEndDate = enteredByFilter.enteredByEndDate;
      }
      const response = await axios.get(`invoice/report`, { params });
      setIsFirstTimeLoaded(false);
      setDashBoardData(response.data.result);
      setCreatedPieChartDataLoading(false);
      setEntryPieChartDataLoading(false);
      setDashBoardDataLoading(false);
    } catch (error) {
      setDashBoardDataLoading(false);
      setCreatedPieChartDataLoading(false);
      setEntryPieChartDataLoading(false);
      showError(getErrorMessage(error), toast);
    }
  };

  useEffect(() => {
    const formattedToday = formatFilterDate(
      new Date(new Date().setDate(new Date().getDate() - 1))
    ); // Format as 'YYYY-MM-DD'

    if (isFirstTimeLoaded) {
      fetchDashBoardData(formattedToday, formattedToday);
    }
  }, []);

  useEffect(() => {
    if (
      (createdByFilter.createdByStartDate &&
        createdByFilter.createdByEndDate &&
        pieType === "createdBy") ||
      (enteredByFilter.enteredByStartDate &&
        enteredByFilter.enteredByEndDate &&
        pieType === "enteredBy")
    ) {
      fetchDashBoardData();
    }
  }, [createdByFilter?.createdByEndDate, enteredByFilter?.enteredByEndDate]);

  const formatTodaysDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="tw-container tw-mx-auto tw-p-4">
        <div className="tw-flex tw-justify-between tw-items-center">
          <h2 className="tw-text-2xl tw-font-bold tw-mb-1">
            Invoice Dashboard
          </h2>
          <h1 className="tw-font-semibold">
            As on <span className="tw-font-bold">{formatTodaysDate()}</span>
          </h1>
        </div>

        {dashBoardDataLoading ? (
          <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50 tw-ml-6 tw-mr-2">
            <ProgressSpinner />
          </div>
        ) : (
          <div>
            {/* Cards Section */}
            {dashBoardData && <DashboardCard dashBoardData={dashBoardData} />}
            {/* <hr className="tw-my-4 tw-w-full tw-border-2 tw-border-gray-500 tw-h-[2px] tw-mb-6" /> */}

            <div>
              {/* Bar Charts and DataTable Section */}
              <BarCharts dashBoardData={dashBoardData} />
              <hr className="tw-my-4 tw-w-full tw-border-2 tw-border-gray-500 tw-h-[2px]" />
              {/* Pie Charts Section */}
              <div>
                <PieCharts
                  dashBoardData={dashBoardData}
                  createdByFilter={createdByFilter}
                  setCreatedByFilter={setCreatedByFilter}
                  enteredByFilter={enteredByFilter}
                  setEnteredByFilter={setEnteredByFilter}
                  createdPieChartDataLoading={createdPieChartDataLoading}
                  entryPieChartDataLoading={entryPieChartDataLoading}
                  pieType={pieType}
                  setPieType={setPieType}
                />
              </div>
            </div>
            {dashBoardData?.managerPendings?.length === 0 &&
              dashBoardData?.seniorManagerPendings?.length === 0 &&
              dashBoardData?.directorPendings?.length === 0 && (
                <div className="tw-w-full tw-text-center tw-justify-center tw-mt-4 text-center">
                  <h1>No Records Found</h1>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
